/**
 * SEO — workspace-wide index (Iter 213 Phase 1).
 *
 * Lists every brand's SEO property with a mini health card. Click through
 * to the Brand detail's SEO tab for the full dashboard.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Search, Globe, TrendingUp, AlertTriangle, Play } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { fmtRelative } from "@/lib/format";

export default function SEO() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    api.get(`/workspaces/${activeWorkspaceId}/seo-properties`)
      .then((r) => setRows(r.data || []))
      .finally(() => setBusy(false));
  }, [activeWorkspaceId]);

  return (
    <>
      <PageHeader
        eyebrow="Growth · SEO"
        title="SEO"
        description="Every brand's technical SEO health at a glance. Rank tracking · keyword research · backlink intelligence · competitor gap analysis coming in Phase 2."
        testid="seo-index-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-12">
        {busy ? <div className="text-sm text-muted-foreground">Loading…</div> :
         rows.length === 0 ? (
          <EmptyState
            title="No SEO properties yet"
            description="Open any brand and click the SEO tab to attach a domain — technical audits, PageSpeed, and (soon) rank tracking + backlink intel wire up automatically."
            icon={Search}
            testid="seo-empty"
          />
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="seo-brand-grid">
            {rows.map((p) => (
              <Link key={p.id} to={`/app/brands/${p.brand_id}?tab=seo`}
                    className="border border-border p-4 hover:border-primary/50 transition"
                    data-testid={`seo-card-${p.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-emerald-500" />
                  <div className="font-semibold truncate">{p.brand_name || p.root_url}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate mb-3">{p.root_url}</div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="label-eyebrow text-[9px]">Health</div>
                    <div className={`text-xl font-bold ${p.health_score == null ? "text-muted-foreground" : p.health_score >= 80 ? "text-emerald-600" : p.health_score >= 50 ? "text-amber-600" : "text-red-600"}`}>
                      {p.health_score ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow text-[9px]">Issues</div>
                    <div className="text-xl font-bold">{p.issues_count ?? 0}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow text-[9px]">Pages</div>
                    <div className="text-xl font-bold">{p.pages_crawled ?? 0}</div>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground mt-2">
                  {p.last_audit_at ? `Last audit ${fmtRelative(p.last_audit_at)}` : <span className="inline-flex items-center gap-1"><Play className="w-3 h-3" /> Never audited — open to run</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
